import React, { useId, useState } from 'react';
import moment from 'moment';
import { generatePath, NavLink } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useVoicemailWithCtiContact } from 'flow/hooks/useVoicemailWithCtiContact';
import { setVoiceMessageRead } from 'flow/actions/extension-actions';
import { useCallRecordDisplayTimestamp } from 'flow/hooks/useCallRecordDisplayTimestamp';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { useModal } from 'flow/contexts/ModalProvider';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import VoicemailRowMenu from './VoicemailRowMenu';
import Icon from 'flow/components/ui-components/Icon';
import { useRowTabIndex } from 'flow/components/ui-components/row-tab-stop';
import AudioPlayerV2 from 'flow/components/pbx/v2/misc/AudioPlayerV2';
import BadgeVoiceMessageUnread from 'flow/components/pbx/v2/misc/BadgeVoiceMessageUnread';
import ContactSubtitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactSubtitle';
import ContactTitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactTitle';
import { TypedMap } from 'flow/types/TypedMap';
import { ExtensionVoiceMessage } from 'flow/types/VoiceMessage';
import { voiceMessageUrlPath } from 'flow/utils';
import { routes } from 'flow/types/routes';

interface Props {
    voiceMessage: TypedMap<ExtensionVoiceMessage>;
    isSelected: boolean;
}

const VoiceMessageItemV2 = ({
    voiceMessage: rawVoiceMessage,
    isSelected,
}: Props) => {
    const dispatch = useAppDispatch();
    const rowTabIndex = useRowTabIndex();
    const callerNameId = useId();
    const { openModal } = useModal();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const voiceMessage =
        useVoicemailWithCtiContact(rawVoiceMessage) ?? rawVoiceMessage;

    const callRecordKey = voiceMessage.get('call');
    // Use the same timestamp the main call-history list shows for this record;
    // fall back to receivedDate if the call record isn't in the store yet.
    const displayTimestamp =
        useCallRecordDisplayTimestamp(callRecordKey, 'personal') ??
        voiceMessage.get('receivedDate');
    const voiceMessageIsUnread =
        voiceMessage.get('voiceMessageState') === 'unread';
    const phoneNumber = voiceMessage.getIn(['phoneNumber', 'number']);
    const isPrivateNumber =
        !phoneNumber ||
        phoneNumber === 'unknown' ||
        phoneNumber === 'anonymous';

    const contact = voiceMessage.get('contact');
    const contactFullName = [
        contact?.get('firstName'),
        contact?.get('lastName'),
    ]
        .join(' ')
        .trim();

    const handlePlayPause = (value: 'play' | 'pause') => {
        if (value === 'play' && voiceMessageIsUnread) {
            dispatch(
                setVoiceMessageRead(
                    ownExtensionKey,
                    callRecordKey,
                    voiceMessage.get('uniqueId'),
                ),
            );
        }
    };

    const handleCreateContact = () => {
        openModal('createContact', { contact });
    };

    const title = (
        <ContactTitle
            contact={contact}
            contactFullName={contactFullName}
            number={voiceMessage.get('phoneNumber')}
        />
    );

    // Who called, and when. Only the caller's name inside it is the link, so
    // a screen reader hears the link as the caller, not as the whole row.
    const identity = (
        <div className="flex">
            <div className="flex h-6 items-center">
                <Icon
                    className="me-2 size-6 text-gray-900"
                    iconKey="voicemail"
                />
            </div>

            <div className="flex w-full min-w-0 flex-col leading-6">
                {/* Title row */}
                <div className="flex min-h-6 items-center justify-between gap-x-2">
                    <div
                        id={callerNameId}
                        className="text-red flex min-w-0 items-center gap-1 font-bold antialiased"
                    >
                        {callRecordKey ? (
                            <NavLink
                                tabIndex={rowTabIndex}
                                className="stretched-control flex min-w-0"
                                to={generatePath(
                                    routes['calls.callRecordKey'],
                                    { callRecordKey },
                                )}
                            >
                                {title}
                            </NavLink>
                        ) : (
                            title
                        )}
                    </div>

                    {/* Time (visible by default, hidden on hover or focus) */}
                    <div
                        className={twMerge(
                            'items-center gap-1 text-sm whitespace-nowrap text-gray-500 antialiased',
                            contextMenuIsActive
                                ? 'hidden'
                                : 'group-focus-visible-within:hidden flex group-hover:hidden',
                        )}
                    >
                        {moment(displayTimestamp).format('HH:mm')}
                    </div>
                </div>

                {/* Subtitle */}
                <div
                    className={twMerge(
                        'text-sm leading-6 text-gray-500 antialiased',
                        'truncate',
                    )}
                >
                    <ContactSubtitle
                        contact={contact}
                        contactFullName={contactFullName}
                        number={voiceMessage.get('phoneNumber')}
                    />
                </div>
            </div>
        </div>
    );

    // The link to the call record, the player and the actions are siblings in
    // the row. A player inside the link would make Enter on its slider follow
    // the link. The player comes before the actions, so Tab reaches Play right
    // after the link. A voicemail without a call record has no link, and Play
    // is then the row's first control, which takes the list's keys.
    return (
        <div
            className={twMerge(
                'group relative flex min-h-12 flex-auto flex-col gap-1 rounded-sm p-2',
                'whitespace-nowrap hover:bg-gray-200',
                contextMenuIsActive && 'bg-gray-100',
                isSelected && 'bg-gray-200 hover:bg-gray-200',
            )}
        >
            <BadgeVoiceMessageUnread visible={voiceMessageIsUnread} />

            {identity}

            <div className="relative ms-8 mt-1">
                <AudioPlayerV2
                    url={voiceMessageUrlPath(
                        ownExtensionKey,
                        voiceMessage.get('uniqueId'),
                    )}
                    duration={voiceMessage.get('durationInSeconds')}
                    onClickPlayPause={handlePlayPause}
                    playDescribedBy={callerNameId}
                    className={twMerge(
                        'group-hover:bg-gray-300',
                        isSelected && 'bg-gray-300',
                    )}
                />
            </div>

            {/* Actions (hidden by default, visible on hover or focus) */}
            <div
                className={twMerge(
                    'absolute end-2 top-2 items-center gap-x-2',
                    contextMenuIsActive
                        ? 'flex'
                        : 'sr-only-until-hover-or-focus flex',
                )}
            >
                {!isPrivateNumber && (
                    <CallAdaptiveButton
                        phoneNumber={phoneNumber}
                        transferContact={contact}
                        size="small"
                        activeBg={true}
                    />
                )}
                <VoicemailRowMenu
                    voiceMessage={voiceMessage}
                    onClickCreateContact={handleCreateContact}
                    setActive={setContextMenuIsActive}
                />
            </div>
        </div>
    );
};

export default React.memo(VoiceMessageItemV2);
